import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, del, get, patch, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Loading,
  Notice,
  Pagination,
} from '../components/ui.js';
import { AUDIO_STATE, formatBytes, formatDateTime, formatDuration } from '../lib/format.js';
import { BELL_SEQUENCE_TRACKS, trackCode, trackLabel } from '../lib/tracks.js';
import { AudioRecorder } from '../components/AudioRecorder.js';

interface AudioAsset {
  id: string;
  original_name: string;
  state: string;
  category: string;
  is_demo: boolean;
  failure_reason: string | null;
  duration_ms: number | null;
  bytes: number | null;
  sha256: string | null;
  created_at: string;
  used_by_rules: number;
  track_number: number | null;
  bell_active: boolean;
}

interface TrackInfo {
  next_free: number | null;
  max_track: number;
  used: Array<{ track: number; asset_id: string; name: string }>;
}

interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function AudioPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadTrack, setUploadTrack] = useState('auto');
  const [recording, setRecording] = useState(false);
  const [preview, setPreview] = useState<{ id: string; name: string } | null>(null);
  const [toDelete, setToDelete] = useState<AudioAsset | null>(null);

  const assets = useQuery({
    queryKey: ['audio', page, search],
    queryFn: () => get<Page<AudioAsset>>(`/audio${qs({ page, page_size: 25, q: search })}`),
    // Poll while anything is still converting.
    refetchInterval: (query) =>
      (query.state.data as Page<AudioAsset> | undefined)?.items.some(
        (a) => a.state === 'UPLOADED' || a.state === 'TRANSCODING',
      )
        ? 2000
        : false,
  });

  const tracks = useQuery({
    queryKey: ['audio', 'tracks'],
    queryFn: () => get<TrackInfo>('/audio/tracks'),
  });
  const usedBy = new Map((tracks.data?.used ?? []).map((u) => [u.track, u]));
  const nextFree = tracks.data?.next_free ?? null;
  // Offer the numbers in use plus some room, not all 255 in every row.
  const highestUsed = Math.max(0, ...usedBy.keys());
  const pickerMax = Math.min(tracks.data?.max_track ?? 255, Math.max(50, highestUsed + 10, (nextFree ?? 0) + 20));

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['audio'] });
  };

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      // Fields must come before the file to reach the server.
      form.append('track', uploadTrack);
      form.append('file', file);
      return api('/audio', { method: 'POST', body: form });
    },
    onSuccess: () => {
      setError(null);
      setUploadTrack('auto');
      refresh();
    },
    onError: (e: Error) => setError(e.message),
    onSettled: () => setUploading(false),
  });

  const setTrack = useMutation({
    mutationFn: ({ id, track }: { id: string; track: number | null }) =>
      patch(`/audio/${id}`, { track_number: track }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<{ archived: boolean; note?: string }>(`/audio/${id}`),
    onSuccess: (res) => {
      setToDelete(null);
      if (res?.note) setError(res.note);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    upload.mutate(file);
    e.target.value = '';
  };

  const trackOptions = (selfId: string): JSX.Element[] => {
    const out: JSX.Element[] = [];
    for (let n = 1; n <= pickerMax; n++) {
      const owner = usedBy.get(n);
      out.push(
        <option key={n} value={n}>
          {trackLabel(n)}
          {owner && owner.asset_id !== selfId ? ' · يستبدل الحالي' : ''}
        </option>,
      );
    }
    return out;
  };

  return (
    <>
      {error ? <Notice tone="warn">{error}</Notice> : null}

      <Card
        title="مكتبة الأصوات"
        subtitle={`${assets.data?.total ?? 0} ملف`}
        actions={
          <>
            <input
              placeholder="بحث بالاسم"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: 200 }}
            />
            {can('audio.manage') ? (
              <>
                <select
                  value={uploadTrack}
                  onChange={(e) => setUploadTrack(e.target.value)}
                  style={{ width: 250 }}
                  aria-label="رقم الملف على جهاز الجرس"
                  title="رقم الملف على جهاز الجرس"
                >
                  <option value="auto">
                    رقم الجرس: تلقائي{nextFree ? ` (${trackCode(nextFree)} أو رقم الاسم)` : ''}
                  </option>
                  <optgroup label="استبدال صوت في تسلسل الجرس">
                    {Array.from({ length: BELL_SEQUENCE_TRACKS }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {trackLabel(n)}
                      </option>
                    ))}
                  </optgroup>
                  <option value="none">بدون رقم (لا يصل إلى الجرس)</option>
                </select>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".wav,.mp3,.m4a,audio/wav,audio/mpeg,audio/mp4"
                  onChange={onPick}
                  className="sr-only"
                  aria-label="اختيار ملف صوتي"
                />
                <button onClick={() => setRecording(true)}>● تسجيل من المايك</button>
                <button
                  className="primary"
                  disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                >
                  {uploading ? 'جارٍ الرفع…' : 'رفع ملف صوتي'}
                </button>
              </>
            ) : null}
          </>
        }
      >
        <Notice tone="info">
          المقبول: WAV و MP3 و M4A حتى 50 م.ب و10 دقائق. يُتحقق من المحتوى الفعلي لا من الامتداد،
          ويُحوَّل الملف إلى صيغة الجهاز (WAV PCM 16-bit، أحادي، 22050 هرتز).
          <strong> المعاينة هنا تعمل في متصفحك فقط ولا تشغّل سماعات المدرسة.</strong>
          <br />
          «رقم الجرس» هو اسم الملف على بطاقة جهاز الجرس: 0001–0021 أصوات التسلسل (الاستبدال يغيّر صوت
          الجرس)، ومن 0022 أصوات إضافية للتشغيل اليدوي. الملف الأحدث تعيينًا لرقم هو المستخدم، ويصل إلى
          أجهزة النسخة 3 تلقائيًا؛ ولأجهزة النسخة 2 استخدم «تنزيل للبطاقة».
        </Notice>

        {assets.isLoading ? (
          <Loading />
        ) : assets.error ? (
          <Failure error={assets.error} onRetry={() => void assets.refetch()} />
        ) : assets.data!.items.length === 0 ? (
          <Empty
            title="المكتبة فارغة"
            hint="ارفع جرسًا أو نشيدًا أو رسالة إخلاء معتمدة لتستخدمها في الجداول."
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الاسم</th>
                    <th>رقم الجرس</th>
                    <th>الحالة</th>
                    <th>المدة</th>
                    <th>الحجم</th>
                    <th>الاستعمالات</th>
                    <th>تاريخ الرفع</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {assets.data!.items.map((a) => {
                    const st = AUDIO_STATE[a.state] ?? AUDIO_STATE.UPLOADED!;
                    return (
                      <tr key={a.id}>
                        <td>
                          {a.original_name}
                          {a.is_demo ? (
                            <>
                              {' '}
                              <Badge tone="warn" icon="⚑">
                                نغمة تجريبية
                              </Badge>
                            </>
                          ) : null}
                          {a.sha256 ? (
                            <div
                              className="mono"
                              style={{ fontSize: 11, color: 'var(--muted)' }}
                              title={a.sha256}
                            >
                              {a.sha256.slice(0, 16)}…
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {can('audio.manage') ? (
                            <select
                              value={a.track_number ?? ''}
                              disabled={setTrack.isPending}
                              onChange={(e) =>
                                setTrack.mutate({
                                  id: a.id,
                                  track: e.target.value === '' ? null : Number(e.target.value),
                                })
                              }
                              style={{ width: 200 }}
                              aria-label={`رقم الجرس للملف ${a.original_name}`}
                            >
                              <option value="">بدون رقم</option>
                              {trackOptions(a.id)}
                            </select>
                          ) : (
                            <span>{a.track_number !== null ? trackLabel(a.track_number) : '—'}</span>
                          )}
                          {a.track_number !== null && a.state === 'READY' ? (
                            <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {a.bell_active ? (
                                <Badge tone="ok" icon="🔔">
                                  على أجهزة الجرس
                                </Badge>
                              ) : (
                                <>
                                  <Badge tone="muted" icon="↺">
                                    ملف أحدث يستخدم الرقم
                                  </Badge>
                                  {can('audio.manage') ? (
                                    <button
                                      className="ghost"
                                      disabled={setTrack.isPending}
                                      onClick={() => setTrack.mutate({ id: a.id, track: a.track_number })}
                                    >
                                      استخدم هذا
                                    </button>
                                  ) : null}
                                </>
                              )}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <Badge tone={st.tone} icon={st.icon}>
                            {st.label}
                          </Badge>
                          {a.failure_reason ? (
                            <div style={{ fontSize: 12, color: 'var(--danger)' }}>
                              {a.failure_reason}
                            </div>
                          ) : null}
                        </td>
                        <td className="num">{formatDuration(a.duration_ms)}</td>
                        <td className="num">{formatBytes(a.bytes)}</td>
                        <td className="num">{a.used_by_rules}</td>
                        <td style={{ fontSize: 12.5 }}>{formatDateTime(a.created_at)}</td>
                        <td className="actions">
                          {a.state === 'READY' ? (
                            <button
                              className="ghost"
                              onClick={() => setPreview({ id: a.id, name: a.original_name })}
                            >
                              معاينة
                            </button>
                          ) : null}
                          {a.state === 'READY' && a.track_number !== null ? (
                            <a
                              href={`/api/v1/audio/${a.id}/card-file`}
                              download
                              title="لنسخه يدويًا إلى مجلد mp3 في بطاقة وحدة MP3 (أجهزة النسخة 2)"
                              style={{ fontSize: 13, whiteSpace: 'nowrap' }}
                            >
                              تنزيل للبطاقة
                            </a>
                          ) : null}
                          {can('audio.manage') ? (
                            <button className="ghost" onClick={() => setToDelete(a)}>
                              أرشفة
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={assets.data!.page}
              totalPages={assets.data!.total_pages}
              total={assets.data!.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      {recording ? (
        <AudioRecorder onClose={() => setRecording(false)} onSaved={refresh} canPlay={can('playback.manual')} />
      ) : null}

      {preview ? (
        <Card title={`معاينة: ${preview.name}`} actions={<button onClick={() => setPreview(null)}>إغلاق</button>}>
          <Notice tone="warn">
            هذه معاينة في متصفحك فقط. لتشغيل الصوت في المدرسة استخدم صفحة «التشغيل اليدوي».
          </Notice>
          {/* Same-origin, cookie-authenticated: the URL is not a bearer secret. */}
          <audio
            controls
            style={{ width: '100%' }}
            src={`/api/v1/audio/${preview.id}/versions/standard/content`}
          >
            متصفحك لا يدعم تشغيل الصوت.
          </audio>
        </Card>
      ) : null}

      {toDelete ? (
        <ConfirmDialog
          title="أرشفة الملف الصوتي"
          danger
          busy={remove.isPending}
          confirmLabel="أرشفة"
          message={
            <>
              سيُخفى «{toDelete.original_name}» من المكتبة.
              {toDelete.track_number !== null && toDelete.bell_active ? (
                <> وسيُحذف المقطع {trackCode(toDelete.track_number)} من أجهزة الجرس ما لم يكن هناك ملف آخر بنفس الرقم.</>
              ) : null}
              {toDelete.used_by_rules > 0 ? (
                <>
                  {' '}
                  هذا الملف مستخدم في <span className="num">{toDelete.used_by_rules}</span> قاعدة
                  جدول وسيُرفض الحذف حتى تزيل الاستخدام.
                </>
              ) : (
                ' لن تُحذف بايتات الملف إذا كان مشارًا إليه من إصدار نشر فعّال، حفاظًا على إمكانية الاسترجاع.'
              )}
            </>
          }
          onCancel={() => setToDelete(null)}
          onConfirm={() => remove.mutate(toDelete.id)}
        />
      ) : null}
    </>
  );
}
